import React, {useState, useEffect} from 'react';
import {
  TextInput,
  View,
  StyleSheet,
  NativeSyntheticEvent,
  TextInputFocusEventData,
} from 'react-native';
import colors from 'theme/Colors';
import {spacing, corner_radius} from 'theme/Units';
import {Text} from 'ui-kits';

interface TextAreaProps {
  label: string;
  helperLabel?: string;
  placeholder?: string;
  error?: string;
  required?: boolean;
  width?: number;
  onFocus?: (e: NativeSyntheticEvent<TextInputFocusEventData>) => void;
  onBlur?: (e: NativeSyntheticEvent<TextInputFocusEventData>) => void;
  value?: string;
  maxCharacters?: number;
  onChangeText?: (text: string) => void;
}

export const TextArea: React.FC<TextAreaProps> = ({
  label,
  placeholder,
  helperLabel,
  error,
  required = false,
  width,
  onFocus,
  onBlur,
  value,
  onChangeText,
  maxCharacters = 500,
}) => {
  const [inputState, setInputState] = useState<'default' | 'focus' | 'error'>(
    'default',
  );

  function onFocusHandler(e: NativeSyntheticEvent<TextInputFocusEventData>) {
    setInputState('focus');
    onFocus?.(e);
  }

  function onBlurHandler(e: NativeSyntheticEvent<TextInputFocusEventData>) {
    setInputState(error ? 'error' : 'default');
    onBlur?.(e);
  }

  useEffect(() => {
    if (error) {
      setInputState('error');
    } else {
      setInputState('default');
    }
  }, [error]);

  return (
    <View style={[styles.inputContainer, {maxWidth: width}]}>
      <Text style={styles.label}>
        {label} {required && <Text style={styles.required}>*</Text>}
      </Text>
      <View
        style={[
          styles.inputWrapper,
          inputState === 'error' && styles.errorBorder,
          inputState === 'focus' && styles.focusBorder,
        ]}>
        <TextInput
          style={styles.textArea}
          placeholder={placeholder}
          placeholderTextColor={colors.palette.nuetral4}
          multiline
          numberOfLines={4}
          onFocus={onFocusHandler}
          onBlur={onBlurHandler}
          value={value}
          onChangeText={onChangeText}
          maxLength={maxCharacters}
        />
      </View>
      {helperLabel && <Text style={styles.helperLabel}>{helperLabel}</Text>}
      {error && <Text style={styles.errorLabel}>{error}</Text>}
      <Text style={styles.characterCounter}>
        {value?.length}/{maxCharacters}
      </Text>
    </View>
  );
};

const styles = StyleSheet.create({
  inputContainer: {
    gap: spacing[2],
    width: '100%',
  },
  label: {
    color: colors.palette.black,
    fontWeight: '600',
    fontSize: 16,
  },
  required: {
    color: colors.palette.errorRedDark,
  },
  helperLabel: {
    color: colors.palette.secondaryBlack5,
    fontSize: 12,
    fontWeight: '400',
  },
  errorLabel: {
    color: colors.palette.errorRedDark,
    fontSize: 12,
    fontWeight: '400',
  },
  inputWrapper: {
    flexDirection: 'row',
    padding: spacing[2],
    borderRadius: corner_radius.rounded,
    backgroundColor: colors.palette.white,
    borderWidth: 2,
    borderColor: colors.palette.stroke,
  },
  textArea: {
    flex: 1,
    minHeight: 80,
    fontWeight: '500',
    color: colors.palette.black,
    outlineColor: 'transparent',
    backgroundColor: 'transparent',
    textAlignVertical: 'top', // Ensure text starts at the top
  },
  errorBorder: {
    borderColor: colors.palette.errorRedDark,
  },
  focusBorder: {
    borderColor: 'rgba(158, 119, 237, 0.4)',
    borderWidth: 3,
  },
  characterCounter: {
    color: colors.palette.secondaryBlack5,
    fontSize: 12,
    fontWeight: 400,
    alignSelf: 'flex-end',
    bottom: 0,
    transform: 'translate(0px, -4px)',
  },
});
